import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { useSignedStoredUrl } from "@/hooks/useSignedStoredUrl";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  DoorOpen,
  X,
  Upload,
  Loader2,
  FileCheck,
  ExternalLink,
  AlertTriangle,
  EyeOff,
  Eye,
  PenLine,
  FileText,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { validateStreetName } from "@/lib/address";
import { normalizeVirtualTourUrl } from "@/lib/virtual-tour-url";
import { AddressAutocomplete } from "@/components/shared/AddressAutocomplete";
import { SignatureDialog } from "@/components/tenant/SignatureDialog";
import { invalidateOccupancyViews } from "@/hooks/v2/useProperties";

// Fallback strings for delete modal (EN/DE) when Storyblok keys are missing
const deleteModalFallbacks: Record<string, Record<string, string>> = {
  en: {
    "admin.deletePropertyTitle": "Delete this property?",
    "admin.deletePropertyConfirmMessage":
      "Are you sure you want to delete this property? This will delete it completely, including all associated rooms and data.",
    "admin.deletePropertyWarning": "This action cannot be undone.",
    "admin.deletePropertyNameLabel": "Property",
    "admin.deleteCancel": "Cancel",
    "admin.deleteConfirmDelete": "Confirm delete",
    "admin.deleting": "Deleting...",
    "admin.signedBy": "Signed by",
    "admin.contractPending": "Contract pending",
    "admin.status": "Status",
    "admin.rent": "Rent",
    "admin.deposit": "Deposit",
    "admin.signed": "signed",
    "admin.pending": "pending",
    "admin.hide": "Hide",
    "admin.unhide": "Unhide",
  },
  de: {
    "admin.deletePropertyTitle": "Diese Immobilie löschen?",
    "admin.deletePropertyConfirmMessage":
      "Sind Sie sicher, dass Sie diese Immobilie löschen möchten? Sie wird vollständig gelöscht, inklusive aller zugehörigen Zimmer und Daten.",
    "admin.deletePropertyWarning":
      "Diese Aktion kann nicht rückgängig gemacht werden.",
    "admin.deletePropertyNameLabel": "Immobilie",
    "admin.deleteCancel": "Abbrechen",
    "admin.deleteConfirmDelete": "Löschen bestätigen",
    "admin.deleting": "Wird gelöscht...",
    "admin.signedBy": "Unterschrieben von",
    "admin.contractPending": "Vertrag ausstehend",
    "admin.status": "Status",
    "admin.rent": "Miete",
    "admin.deposit": "Kaution",
    "admin.signed": "unterschrieben",
    "admin.pending": "ausstehend",
    "admin.hide": "Ausblenden",
    "admin.unhide": "Einblenden",
  },
};

export const AdminProperties = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const tr = (key: string) =>
    t(key) ||
    deleteModalFallbacks[language]?.[key] ||
    deleteModalFallbacks.en[key] ||
    "";
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState<any>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(
    null
  );
  const [propertyProfileOpen, setPropertyProfileOpen] = useState(false);
  const [propertyProfileId, setPropertyProfileId] = useState<number | null>(
    null
  );
  const [editingRoom, setEditingRoom] = useState<any>(null);
  const [roomFormData, setRoomFormData] = useState({
    title: "",
    price: 0,
    available: true,
    description: "",
    images: "[]",
  });
  const [roomImageFiles, setRoomImageFiles] = useState<File[]>([]);
  const [roomImagePreviews, setRoomImagePreviews] = useState<string[]>([]);
  const [propertyImageFile, setPropertyImageFile] = useState<File | null>(null);
  const [propertyImagePreview, setPropertyImagePreview] = useState<
    string | null
  >(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [formData, setFormData] = useState({
    title: "",
    city: "",
    address: "",
    lat: 0,
    lng: 0,
    description: "",
    image_url: "",
    virtual_tour_url: "",
    default_contract_template_id: "" as string,
    leasor_signature_url: "" as string,
  });
  // The signature preview is an <img>; its src is a signed URL, never the
  // stored public one (COL-436).
  const landlordSignature = useSignedStoredUrl(
    formData.leasor_signature_url || null,
    "contracts"
  );

  /**
   * Contract-placeholder fields live in a SEPARATE modal so editing them
   * doesn't go through manage-properties → Storyblok (these are contract-only
   * values that have no place in the CMS).  Fields mirror the contract
   * template placeholder palette: OWNER_NAME, REPRESENTOR_NAME,
   * REPRESENTOR_CITY, PROPERTY_STREET_NAME/NUMBER, PROPERTY_ZIP_CODE,
   * PROPERTY_CITY_NAME (on `properties`) plus the bank quartet on
   * `property_payment_config`.
   */
  const [contractFieldsOpen, setContractFieldsOpen] = useState(false);
  const [contractFieldsProperty, setContractFieldsProperty] =
    useState<any>(null);
  const [savingContractFields, setSavingContractFields] = useState(false);
  const [contractFieldsData, setContractFieldsData] = useState({
    leasor_name: "",
    representor_name: "",
    representor_city: "",
    street_name: "",
    street_number: "",
    zip_code: "",
    city_name: "",
    bank_account_holder: "",
    bank_iban: "",
    bank_bic: "",
    bank_name: "",
  });
  /** Controls the in-dialog signature capture modal for the landlord signature. */
  const [leasorSignatureDialogOpen, setLeasorSignatureDialogOpen] =
    useState(false);
  const [uploadingLeasorSignature, setUploadingLeasorSignature] =
    useState(false);

  const handleViewPropertyProfile = (propertyId: number) => {
    setPropertyProfileId(propertyId);
    setPropertyProfileOpen(true);
  };

  const { data: properties, isLoading } = useQuery({
    queryKey: ["admin-properties"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      // Deduplicate properties by title + city + address (keep the most recent one)
      if (data) {
        const seen = new Map<string, any>();
        const deduplicated = data.filter((prop: any) => {
          const key =
            `${prop.title}|${prop.city}|${prop.address}`.toLowerCase();
          if (seen.has(key)) {
            // Keep the one with the latest created_at
            const existing = seen.get(key);
            if (new Date(prop.created_at) > new Date(existing.created_at)) {
              seen.set(key, prop);
              return true;
            }
            return false;
          } else {
            seen.set(key, prop);
            return true;
          }
        });
        return deduplicated;
      }

      return data;
    },
  });

  // Contract templates for the property default template dropdown
  const { data: contractTemplates = [] } = useQuery({
    queryKey: ["contract-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_templates")
        .select("id, name, is_default")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch all rooms with property info for the main list (one row per room, like public properties page)
  const { data: allRoomsWithProperties, isLoading: isLoadingRooms } = useQuery({
    queryKey: ["admin-all-rooms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rooms")
        .select(
          `
          *,
          properties (
            id,
            title,
            city,
            address,
            image_url,
            is_hidden,
            created_at
          )
        `
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch rooms for selected property (dialog)
  const { data: rooms } = useQuery({
    queryKey: ["admin-rooms", selectedPropertyId],
    queryFn: async () => {
      if (!selectedPropertyId) return [];
      const { data, error } = await supabase
        .from("rooms")
        .select("*")
        .eq("property_id", selectedPropertyId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedPropertyId,
  });

  // Fetch all rooms (property_id + images) for list thumbnails
  const { data: allRoomsForThumbnails } = useQuery({
    queryKey: ["admin-all-rooms-thumbnails"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rooms")
        .select("id, property_id, images")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch all bookings with room and property (for booked/signed, rent, deposit, signer)
  const { data: bookings } = useQuery({
    queryKey: ["admin-properties-bookings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          id,
          room_id,
          user_id,
          name,
          email,
          status,
          contract_signed_at,
          monthly_rent,
          deposit_amount,
          rooms (
            id,
            title,
            property_id,
            properties (
              id,
              title,
              city,
              address
            )
          )
        `
        )
        .in("status", ["pending", "confirmed"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch profiles for tenant names (who signed)
  const { data: profiles } = useQuery({
    queryKey: ["admin-all-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, name, email")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Map property_id -> list of bookings (signed/confirmed) for this property
  const propertyBookingsMap = useMemo(() => {
    const map = new Map<number, any[]>();
    if (!bookings) return map;
    for (const b of bookings) {
      const propertyId =
        (b.rooms as any)?.property_id ?? (b.rooms as any)?.properties?.id;
      if (propertyId == null) continue;
      if (!map.has(propertyId)) map.set(propertyId, []);
      map.get(propertyId)!.push(b);
    }
    return map;
  }, [bookings]);

  const profileMap = useMemo(() => {
    return new Map((profiles || []).map((p: any) => [p.id, p]));
  }, [profiles]);

  // For each property: signed vs pending signers (separate for clear picture), rent range, deposit range
  const propertySummaryMap = useMemo(() => {
    const summary: Record<
      number,
      {
        signedCount: number;
        pendingCount: number;
        signedSignerDetails: {
          name: string;
          userId?: string;
          bookingId: number;
        }[];
        pendingSignerDetails: {
          name: string;
          userId?: string;
          bookingId: number;
        }[];
        rents: number[];
        deposits: number[];
      }
    > = {};
    propertyBookingsMap.forEach((list, propertyId) => {
      const signed = list.filter((b: any) => b.contract_signed_at);
      const pending = list.filter(
        (b: any) => !b.contract_signed_at && b.status !== "cancelled"
      );
      const signedSignerDetails: {
        name: string;
        userId?: string;
        bookingId: number;
      }[] = signed.map((b: any) => {
        const name = b.user_id
          ? (profileMap.get(b.user_id)?.name ??
            profileMap.get(b.user_id)?.email ??
            "")
          : b.name || b.email || "";
        return { name: name || "Unknown", userId: b.user_id, bookingId: b.id };
      });
      const pendingSignerDetails: {
        name: string;
        userId?: string;
        bookingId: number;
      }[] = pending.map((b: any) => {
        const name = b.user_id
          ? (profileMap.get(b.user_id)?.name ??
            profileMap.get(b.user_id)?.email ??
            "")
          : b.name || b.email || "";
        return { name: name || "Unknown", userId: b.user_id, bookingId: b.id };
      });
      const rents: number[] = [];
      const deposits: number[] = [];
      list.forEach((b: any) => {
        if (b.monthly_rent != null) rents.push(Number(b.monthly_rent));
        if (b.deposit_amount != null) deposits.push(Number(b.deposit_amount));
      });
      summary[propertyId] = {
        signedCount: signed.length,
        pendingCount: pending.length,
        signedSignerDetails,
        pendingSignerDetails,
        rents,
        deposits,
      };
    });
    return summary;
  }, [propertyBookingsMap, profileMap]);

  // Map room_id -> list of bookings for this room (for per-row status/signed/rent/deposit)
  const roomBookingsMap = useMemo(() => {
    const map = new Map<number, any[]>();
    if (!bookings) return map;
    for (const b of bookings) {
      const roomId = b.room_id;
      if (roomId == null) continue;
      if (!map.has(roomId)) map.set(roomId, []);
      map.get(roomId)!.push(b);
    }
    return map;
  }, [bookings]);

  // Per-room summary (signed/pending, signers, rents, deposits) for table rows
  const roomSummaryMap = useMemo(() => {
    const summary: Record<
      number,
      {
        signedCount: number;
        pendingCount: number;
        signedSignerDetails: {
          name: string;
          userId?: string;
          bookingId: number;
        }[];
        pendingSignerDetails: {
          name: string;
          userId?: string;
          bookingId: number;
        }[];
        rents: number[];
        deposits: number[];
      }
    > = {};
    roomBookingsMap.forEach((list, roomId) => {
      const signed = list.filter((b: any) => b.contract_signed_at);
      const pending = list.filter(
        (b: any) => !b.contract_signed_at && b.status !== "cancelled"
      );
      const signedSignerDetails = signed.map((b: any) => {
        const name = b.user_id
          ? (profileMap.get(b.user_id)?.name ??
            profileMap.get(b.user_id)?.email ??
            "")
          : b.name || b.email || "";
        return { name: name || "Unknown", userId: b.user_id, bookingId: b.id };
      });
      const pendingSignerDetails = pending.map((b: any) => {
        const name = b.user_id
          ? (profileMap.get(b.user_id)?.name ??
            profileMap.get(b.user_id)?.email ??
            "")
          : b.name || b.email || "";
        return { name: name || "Unknown", userId: b.user_id, bookingId: b.id };
      });
      const rents: number[] = [];
      const deposits: number[] = [];
      list.forEach((b: any) => {
        if (b.monthly_rent != null) rents.push(Number(b.monthly_rent));
        if (b.deposit_amount != null) deposits.push(Number(b.deposit_amount));
      });
      summary[roomId] = {
        signedCount: signed.length,
        pendingCount: pending.length,
        signedSignerDetails,
        pendingSignerDetails,
        rents,
        deposits,
      };
    });
    return summary;
  }, [roomBookingsMap, profileMap]);

  // Fix double-prefixed Storyblok URLs (sync bug: url stored as https://a.storyblok.com/f/https://a.storyblok.com/f/...)
  const fixStoryblokImageUrl = (
    url: string | null | undefined
  ): string | null => {
    if (!url || typeof url !== "string") return null;
    const double = "https://a.storyblok.com/f/https://a.storyblok.com/";
    if (url.startsWith(double))
      return "https://a.storyblok.com/" + url.slice(double.length);
    return url;
  };

  // Normalize room.images (string JSON, array, or JSONB object) to array of URL strings
  const normalizeRoomImagesToUrls = (images: unknown): string[] => {
    if (!images) return [];
    let arr: unknown[] = [];
    if (typeof images === "string") {
      try {
        arr = JSON.parse(images || "[]");
      } catch {
        return [];
      }
    } else if (Array.isArray(images)) {
      arr = images;
    } else if (typeof images === "object" && images !== null) {
      arr = Object.values(images);
    }
    return arr
      .map((v) => {
        if (typeof v === "string") {
          const u = v.startsWith("http") ? v : `https://a.storyblok.com/f/${v}`;
          return fixStoryblokImageUrl(u) || u;
        }
        if (
          v &&
          typeof v === "object" &&
          "filename" in v &&
          typeof (v as any).filename === "string"
        ) {
          const u = (v as any).filename.startsWith("http")
            ? (v as any).filename
            : `https://a.storyblok.com/f/${(v as any).filename}`;
          return fixStoryblokImageUrl(u) || u;
        }
        if (
          v &&
          typeof v === "object" &&
          "url" in v &&
          typeof (v as any).url === "string"
        )
          return fixStoryblokImageUrl((v as any).url) || (v as any).url;
        return null;
      })
      .filter((v): v is string => !!v);
  };

  // Property + room thumbnail URLs for list (property image first, then room images, max 5)
  const propertyThumbnailsMap = useMemo(() => {
    const map: Record<number, string[]> = {};
    if (!allRoomsForThumbnails) return map;
    allRoomsForThumbnails.forEach((room: any) => {
      const pid = room.property_id;
      if (!map[pid]) map[pid] = [];
      const urls = normalizeRoomImagesToUrls(room.images);
      urls.forEach((url) => {
        if (url && !map[pid].includes(url)) map[pid].push(url);
      });
    });
    return map;
  }, [allRoomsForThumbnails]);

  // Rooms list for table: exclude rooms whose property is hidden
  const listRooms = useMemo(() => {
    if (!allRoomsWithProperties || !Array.isArray(allRoomsWithProperties))
      return [];
    return allRoomsWithProperties.filter((room: any) => {
      const prop = room.properties;
      return !prop || prop.is_hidden !== true;
    });
  }, [allRoomsWithProperties]);

  // Pagination (by room)
  const totalPages = Math.max(
    1,
    Math.ceil((listRooms?.length || 0) / itemsPerPage)
  );
  const paginatedRooms = useMemo(() => {
    if (!listRooms || listRooms.length === 0) return [];
    const start = (currentPage - 1) * itemsPerPage;
    return listRooms.slice(start, start + itemsPerPage);
  }, [listRooms, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  /**
   * Upload a landlord-signature PNG to Supabase Storage and stash the public URL
   * into `formData.leasor_signature_url`. The main form save then persists it to
   * the properties row. Path: `property-signatures/{propertyId|new}/{timestamp}.png`
   */
  const handleLeasorSignatureConfirm = async (pngBlob: Blob) => {
    setUploadingLeasorSignature(true);
    try {
      const propertyIdSegment = editingProperty?.id ?? "new";
      const fileName = `property-signatures/${propertyIdSegment}/${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(fileName, pngBlob, { contentType: "image/png", upsert: false });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(fileName);
      setFormData((prev) => ({
        ...prev,
        leasor_signature_url: urlData.publicUrl,
      }));
      setLeasorSignatureDialogOpen(false);
      toast.success(
        t(
          "admin.properties.leasorSigSaved",
          "Landlord signature saved. Click Update to persist on property."
        )
      );
    } catch (err: any) {
      toast.error(
        err?.message ||
          t("admin.properties.uploadSigFailed", "Failed to upload signature.")
      );
    } finally {
      setUploadingLeasorSignature(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const { data: result, error } = await supabase.functions.invoke(
        "manage-properties",
        {
          body: {
            action: "create_property",
            propertyData: data,
          },
        }
      );

      if (error) throw error;
      if (!result?.success) {
        throw new Error(
          result?.error ||
            t("admin.properties.createFailed", "Failed to create property")
        );
      }

      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });

      if (result?.message?.includes("Storyblok sync failed")) {
        toast.warning(t("admin.propertyCreated"), {
          description: result.message,
        });
      } else {
        toast.success(t("admin.propertyCreated"), {
          description:
            result?.message ||
            t(
              "admin.properties.createdMsg",
              "Property created in Supabase and Storyblok"
            ),
        });
      }

      setOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t("admin.properties.createFailed", "Failed to create property")
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const { data: result, error } = await supabase.functions.invoke(
        "manage-properties",
        {
          body: {
            action: "update_property",
            propertyId: id,
            propertyData: data,
          },
        }
      );

      if (error) throw error;
      if (!result?.success) {
        throw new Error(
          result?.error ||
            t("admin.properties.updateFailed", "Failed to update property")
        );
      }

      return result;
    },
    onSuccess: async (result) => {
      // Invalidate and refetch properties to get updated image URL
      await queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });

      // Refetch the updated property to get the new image URL
      if (editingProperty) {
        const { data: updatedProperty } = await supabase
          .from("properties")
          .select("*")
          .eq("id", editingProperty.id)
          .single();

        // Update form data and preview with the latest property data (including new image URL)
        setFormData((prev) => ({
          ...prev,
          image_url: updatedProperty.image_url || prev.image_url,
        }));

        if (updatedProperty.image_url) {
          console.log(
            "Updated property image preview to:",
            updatedProperty.image_url
          );
          setPropertyImagePreview(updatedProperty.image_url);
        } else {
          // If no image_url, clear preview
          setPropertyImagePreview(null);
        }

        // Update editingProperty with latest data
        setEditingProperty(updatedProperty);
      }

      if (result?.message?.includes("Storyblok sync failed")) {
        toast.warning(t("admin.propertyUpdated"), {
          description: result.message,
        });
      } else {
        toast.success(t("admin.propertyUpdated"), {
          description:
            result?.message ||
            t(
              "admin.properties.updatedMsg",
              "Property updated in Supabase and Storyblok"
            ),
        });
      }

      // Don't close dialog immediately - let user see the updated image
      // setOpen(false);
      // resetForm();
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t("admin.properties.updateFailed", "Failed to update property")
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const { data: result, error } = await supabase.functions.invoke(
        "manage-properties",
        {
          body: {
            action: "delete_property",
            propertyId: id,
          },
        }
      );

      if (error) throw error;
      if (!result?.success) {
        throw new Error(
          result?.error ||
            t("admin.properties.deleteFailed", "Failed to delete property")
        );
      }

      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);

      if (result?.message?.includes("Storyblok sync failed")) {
        toast.warning(t("admin.propertyDeleted"), {
          description: result.message,
        });
      } else {
        toast.success(t("admin.propertyDeleted"), {
          description:
            result?.message ||
            t(
              "admin.properties.deletedMsg",
              "Property and all rooms deleted from Supabase and Storyblok"
            ),
        });
      }
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t("admin.properties.deleteFailed", "Failed to delete property")
      );
    },
  });

  const handleDeleteClick = (propertyId: number) => {
    setPropertyToDelete(propertyId);
    setDeleteDialogOpen(true);
  };

  /**
   * Open the "Contract Fields" modal and load current values straight from
   * Supabase (bypassing the Storyblok-synced manage-properties edge function
   * because none of these values belong in the CMS).
   *
   * Bank details live on `room_payment_config` (per-room).  We treat them as
   * property-level in the UI: read from the first room in this property that
   * has any bank value set, and fan out to every room on save.
   */
  const handleOpenContractFields = async (property: any) => {
    setContractFieldsProperty(property);

    const { data: rooms } = await supabase
      .from("rooms")
      .select("id")
      .eq("property_id", property.id);

    const roomIds = (rooms ?? []).map((r: { id: number }) => r.id);
    let paymentCfg: {
      bank_account_holder: string | null;
      bank_iban: string | null;
      bank_bic: string | null;
      bank_name: string | null;
    } | null = null;
    if (roomIds.length > 0) {
      const { data: cfgRows } = await supabase
        .from("room_payment_config")
        .select("bank_account_holder, bank_iban, bank_bic, bank_name")
        .in("room_id", roomIds);
      // Prefer the first row that actually has a bank value set (so we don't
      // load a freshly-seeded empty row over a populated one).
      paymentCfg =
        (cfgRows ?? []).find(
          (r: any) =>
            r.bank_account_holder || r.bank_iban || r.bank_bic || r.bank_name
        ) ??
        (cfgRows ?? [])[0] ??
        null;
    }

    setContractFieldsData({
      leasor_name: property.leasor_name || "",
      representor_name: property.representor_name || "",
      representor_city: property.representor_city || "",
      street_name: property.street_name || "",
      street_number: property.street_number || "",
      zip_code: property.zip_code || "",
      city_name: property.city_name || "",
      bank_account_holder: paymentCfg?.bank_account_holder || "",
      bank_iban: paymentCfg?.bank_iban || "",
      bank_bic: paymentCfg?.bank_bic || "",
      bank_name: paymentCfg?.bank_name || "",
    });
    setContractFieldsOpen(true);
  };

  /**
   * Save contract-placeholder values directly to Supabase — no manage-properties,
   * no Storyblok push.  Writes contract columns to `properties` and fans out
   * the bank quartet to `room_payment_config` (one upsert per room in the
   * property).  One property → one landlord bank account → identical values
   * on every room row.
   */
  const handleSaveContractFields = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contractFieldsProperty) return;

    // Street name is printed verbatim on the contract and on §3 of the
    // Wohnungsgeberbestätigung, so an abbreviation ("Teststr.") is not valid
    // input. Same rule as the V2 property forms — see validateStreetName.
    const streetNameError = validateStreetName(contractFieldsData.street_name);
    if (streetNameError) {
      toast.error(streetNameError);
      return;
    }

    setSavingContractFields(true);
    try {
      const pid = contractFieldsProperty.id;
      const toNull = (v: string) => (v.trim() === "" ? null : v.trim());

      const { error: propErr } = await supabase
        .from("properties")
        .update({
          leasor_name: toNull(contractFieldsData.leasor_name),
          representor_name: toNull(contractFieldsData.representor_name),
          representor_city: toNull(contractFieldsData.representor_city),
          street_name: toNull(contractFieldsData.street_name),
          street_number: toNull(contractFieldsData.street_number),
          zip_code: toNull(contractFieldsData.zip_code),
          city_name: toNull(contractFieldsData.city_name),
        })
        .eq("id", pid);
      if (propErr) throw propErr;

      // Fan out bank details to every room in this property.  room_payment_config
      // has a UNIQUE(room_id) constraint, so the upsert is idempotent.  We
      // only set bank_* columns here — existing payment-method flags
      // (stripe_card_enabled, etc.) on those rows are preserved.
      const { data: propertyRooms, error: roomsErr } = await supabase
        .from("rooms")
        .select("id")
        .eq("property_id", pid);
      if (roomsErr) throw roomsErr;

      const rows = (propertyRooms ?? []).map((r: { id: number }) => ({
        room_id: r.id,
        bank_account_holder: toNull(contractFieldsData.bank_account_holder),
        bank_iban: toNull(contractFieldsData.bank_iban),
        bank_bic: toNull(contractFieldsData.bank_bic),
        bank_name: toNull(contractFieldsData.bank_name),
      }));
      if (rows.length > 0) {
        const { error: payErr } = await supabase
          .from("room_payment_config")
          .upsert(rows, { onConflict: "room_id" });
        if (payErr) throw payErr;
      } else {
        toast.info(
          t(
            "admin.properties.noRoomsBankNote",
            "No rooms in this property yet — bank details saved to the property only."
          )
        );
      }

      toast.success(
        t("admin.properties.contractFieldsSaved", "Contract fields saved")
      );
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      setContractFieldsOpen(false);
      setContractFieldsProperty(null);
    } catch (err: any) {
      console.error("Failed to save contract fields:", err);
      toast.error(
        err?.message ||
          t(
            "admin.properties.contractFieldsSaveFailed",
            "Failed to save contract fields"
          )
      );
    } finally {
      setSavingContractFields(false);
    }
  };

  const hidePropertyMutation = useMutation({
    mutationFn: async (propertyId: number) => {
      const { error } = await supabase
        .from("properties")
        .update({ is_hidden: true })
        .eq("id", propertyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      toast.success(tr("admin.hide"), {
        description: t(
          "admin.properties.nowHidden",
          "Property is now hidden from the public listing."
        ),
      });
    },
    onError: (err: Error) =>
      toast.error(
        err.message ||
          t("admin.properties.hideFailed", "Failed to hide property")
      ),
  });

  const unhidePropertyMutation = useMutation({
    mutationFn: async (propertyId: number) => {
      const { error } = await supabase
        .from("properties")
        .update({ is_hidden: false })
        .eq("id", propertyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      toast.success(tr("admin.unhide"), {
        description: t(
          "admin.properties.nowVisible",
          "Property is now visible on the public listing."
        ),
      });
    },
    onError: (err: Error) =>
      toast.error(
        err.message ||
          t("admin.properties.unhideFailed", "Failed to unhide property")
      ),
  });

  // Hide/unhide this room only (not the whole property)
  const hideRoomMutation = useMutation({
    mutationFn: async (roomId: number) => {
      const { error } = await supabase
        .from("rooms")
        .update({ is_hidden: true })
        .eq("id", roomId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      toast.success(tr("admin.hide"), {
        description: t(
          "admin.rooms.nowHidden",
          "Room is now hidden from the public listing."
        ),
      });
    },
    onError: (err: Error) =>
      toast.error(
        err.message || t("admin.rooms.hideFailed", "Failed to hide room")
      ),
  });

  const unhideRoomMutation = useMutation({
    mutationFn: async (roomId: number) => {
      const { error } = await supabase
        .from("rooms")
        .update({ is_hidden: false })
        .eq("id", roomId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      toast.success(tr("admin.unhide"), {
        description: t(
          "admin.rooms.nowVisible",
          "Room is now visible on the public listing."
        ),
      });
    },
    onError: (err: Error) =>
      toast.error(
        err.message || t("admin.rooms.unhideFailed", "Failed to unhide room")
      ),
  });

  const handleConfirmDelete = () => {
    if (propertyToDelete) {
      deleteMutation.mutate(propertyToDelete);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      city: "",
      address: "",
      lat: 0,
      lng: 0,
      description: "",
      image_url: "",
      virtual_tour_url: "",
      default_contract_template_id: "",
      leasor_signature_url: "",
    });
    setPropertyImageFile(null);
    setPropertyImagePreview(null);
    setEditingProperty(null);
  };

  // Ensure image preview is set when dialog opens for editing
  useEffect(() => {
    if (open && editingProperty) {
      console.log("Dialog opened for property:", editingProperty);
      console.log("Property image_url:", editingProperty.image_url);
      console.log("Current formData.image_url:", formData.image_url);
      console.log("Current propertyImagePreview:", propertyImagePreview);

      // Set preview from editingProperty if it exists and preview is not set
      const imageUrl = editingProperty.image_url || formData.image_url;
      if (imageUrl && !propertyImagePreview) {
        console.log(
          "Setting preview from editingProperty.image_url:",
          imageUrl
        );
        setPropertyImagePreview(imageUrl);
      } else if (!imageUrl && propertyImagePreview) {
        // Clear preview if no image URL
        console.log("Clearing preview - no image URL");
        setPropertyImagePreview(null);
      }
    } else if (!open) {
      // Clear preview when dialog closes
      setPropertyImagePreview(null);
    }
  }, [open, editingProperty]);

  const resetRoomForm = () => {
    setRoomFormData({
      title: "",
      price: 0,
      available: true,
      description: "",
      images: "[]",
    });
    setEditingRoom(null);
  };

  const handleManageRooms = (propertyId: number) => {
    setSelectedPropertyId(propertyId);
    setRoomDialogOpen(true);
  };

  const handleViewProfile = (propertyId: number) => {
    setPropertyProfileId(propertyId);
    setPropertyProfileOpen(true);
  };

  const handleEditRoom = (room: any) => {
    setEditingRoom(room);
    const images =
      typeof room.images === "string"
        ? JSON.parse(room.images || "[]")
        : room.images || [];
    setRoomFormData({
      title: room.title,
      price: room.price,
      available: room.available,
      description: room.description || "",
      images:
        typeof room.images === "string"
          ? room.images
          : JSON.stringify(room.images || []),
    });
    // Set previews for existing images
    setRoomImagePreviews(
      images
        .filter((img: any) => typeof img === "string" && img.startsWith("http"))
        .map((img: any) => img)
    );
    setRoomImageFiles([]);
  };

  // Room mutations
  const createRoomMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!selectedPropertyId) throw new Error("Property ID is required");

      const { data: result, error } = await supabase.functions.invoke(
        "manage-properties",
        {
          body: {
            action: "create_room",
            roomData: {
              property_id: selectedPropertyId,
              title: data.title,
              price: data.price,
              available: data.available,
              description: data.description || null,
              images: data.images,
              imageFiles: data.imageFiles || [],
            },
          },
        }
      );

      if (error) throw error;
      if (!result?.success) {
        throw new Error(
          result?.error ||
            t("admin.rooms.createFailed", "Failed to create room")
        );
      }

      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-rooms", selectedPropertyId],
      });
      resetRoomForm();

      if (result?.message?.includes("Storyblok sync failed")) {
        toast.warning(t("admin.rooms.created", "Room created"), {
          description: result.message,
        });
      } else {
        toast.success(t("admin.rooms.created", "Room created"), {
          description:
            result?.message ||
            t(
              "admin.rooms.createdMsg",
              "Room created in Supabase and Storyblok"
            ),
        });
      }
    },
    onError: (error: any) => {
      toast.error(
        error.message || t("admin.rooms.createFailed", "Failed to create room")
      );
    },
  });

  const updateRoomMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const { data: result, error } = await supabase.functions.invoke(
        "manage-properties",
        {
          body: {
            action: "update_room",
            roomId: id,
            roomData: {
              title: data.title,
              price: data.price,
              available: data.available,
              description: data.description || null,
              images: data.images,
              imageFiles: data.imageFiles || [],
            },
          },
        }
      );

      if (error) throw error;
      if (!result?.success) {
        throw new Error(
          result?.error ||
            t("admin.rooms.updateFailed", "Failed to update room")
        );
      }

      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-rooms", selectedPropertyId],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });
      invalidateOccupancyViews(queryClient);
      queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["admin-analytics-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      resetRoomForm();

      if (result?.message?.includes("Storyblok sync failed")) {
        toast.warning(t("admin.rooms.updated", "Room updated"), {
          description: result.message,
        });
      } else {
        toast.success(t("admin.rooms.updated", "Room updated"), {
          description:
            result?.message ||
            t(
              "admin.rooms.updatedMsg",
              "Room updated in Supabase and Storyblok"
            ),
        });
      }
    },
    onError: (error: any) => {
      toast.error(
        error.message || t("admin.rooms.updateFailed", "Failed to update room")
      );
    },
  });

  const deleteRoomMutation = useMutation({
    mutationFn: async (id: number) => {
      const { data: result, error } = await supabase.functions.invoke(
        "manage-properties",
        {
          body: {
            action: "delete_room",
            roomId: id,
          },
        }
      );

      if (error) throw error;
      if (!result?.success) {
        throw new Error(
          result?.error ||
            t("admin.rooms.deleteFailed", "Failed to delete room")
        );
      }

      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-rooms", selectedPropertyId],
      });

      if (result?.message?.includes("Storyblok sync failed")) {
        toast.warning(t("admin.rooms.deleted", "Room deleted"), {
          description: result.message,
        });
      } else {
        toast.success(t("admin.rooms.deleted", "Room deleted"), {
          description:
            result?.message ||
            t(
              "admin.rooms.deletedMsg",
              "Room deleted from Supabase and Storyblok updated"
            ),
        });
      }
    },
    onError: (error: any) => {
      toast.error(
        error.message || t("admin.rooms.deleteFailed", "Failed to delete room")
      );
    },
  });

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Add new files
    setRoomImageFiles((prev) => [...prev, ...files]);

    // Create previews
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setRoomImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    // Check if it's a new file or existing image
    const totalPreviews = roomImagePreviews.length;
    const newFilesCount = roomImageFiles.length;
    const existingImagesCount = totalPreviews - newFilesCount;

    if (index < existingImagesCount) {
      // Remove existing image - update the images array in formData
      const currentImages =
        typeof roomFormData.images === "string"
          ? JSON.parse(roomFormData.images || "[]")
          : Array.isArray(roomFormData.images)
            ? roomFormData.images
            : [];
      const updatedImages = currentImages.filter(
        (_: any, i: number) => i !== index
      );
      setRoomFormData({
        ...roomFormData,
        images: JSON.stringify(updatedImages),
      });
      setRoomImagePreviews((prev) => prev.filter((_, i) => i !== index));
    } else {
      // Remove new file
      const fileIndex = index - existingImagesCount;
      setRoomImageFiles((prev) => prev.filter((_, i) => i !== fileIndex));
      setRoomImagePreviews((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleRoomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Convert files to base64 for sending to edge function
    const imageDataPromises = roomImageFiles.map((file) => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    let imageBase64Data: string[] = [];
    try {
      imageBase64Data = await Promise.all(imageDataPromises);
    } catch (error) {
      toast.error(
        t("admin.rooms.processImagesFailed", "Failed to process images")
      );
      return;
    }

    const formDataWithImages = {
      ...roomFormData,
      imageFiles: imageBase64Data.map((base64, index) => ({
        data: base64,
        filename: roomImageFiles[index].name,
        contentType: roomImageFiles[index].type,
      })),
    };

    if (editingRoom) {
      updateRoomMutation.mutate({
        id: editingRoom.id,
        data: formDataWithImages,
      });
    } else {
      createRoomMutation.mutate(formDataWithImages);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Convert property image file to base64 if selected
    let imageBase64Data: string | null = null;
    if (propertyImageFile) {
      try {
        imageBase64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(propertyImageFile);
        });
        console.log(
          "✅ Image converted to base64, length:",
          imageBase64Data?.length
        );
      } catch (error) {
        console.error("❌ Failed to process image:", error);
        toast.error(
          t("admin.properties.processImageFailed", "Failed to process image")
        );
        return;
      }
    } else {
      console.log("ℹ️ No property image file selected");
    }

    const formDataWithImage = {
      ...formData,
      virtual_tour_url: normalizeVirtualTourUrl(formData.virtual_tour_url),
      // Convert empty string to null for UUID FK column
      default_contract_template_id:
        formData.default_contract_template_id || null,
      imageFile: imageBase64Data
        ? {
            data: imageBase64Data,
            filename: propertyImageFile!.name,
            contentType: propertyImageFile!.type,
          }
        : null,
    };

    console.log("📤 Submitting property data:", {
      title: formDataWithImage.title,
      hasImageFile: !!formDataWithImage.imageFile,
      imageFileName: formDataWithImage.imageFile?.filename,
      imageFileContentType: formDataWithImage.imageFile?.contentType,
      imageFileDataLength: formDataWithImage.imageFile?.data?.length,
    });

    if (editingProperty) {
      updateMutation.mutate({
        id: editingProperty.id,
        data: formDataWithImage,
      });
    } else {
      createMutation.mutate(formDataWithImage);
    }
  };

  const handleEdit = (property: any) => {
    console.log("Editing property:", property);
    console.log("Property image_url:", property.image_url);
    setEditingProperty(property);
    const imageUrl = property.image_url || "";
    setFormData({
      title: property.title,
      city: property.city,
      address: property.address,
      lat: property.lat,
      lng: property.lng,
      description: property.description || "",
      image_url: imageUrl,
      virtual_tour_url: property.virtual_tour_url || "",
      default_contract_template_id: property.default_contract_template_id || "",
      leasor_signature_url: property.leasor_signature_url || "",
    });
    // Set preview for existing image URL - always set it if image_url exists
    if (imageUrl) {
      console.log("Setting property image preview to:", imageUrl);
      setPropertyImagePreview(imageUrl);
    } else {
      console.log("No image_url found, clearing preview");
      setPropertyImagePreview(null);
    }
    setPropertyImageFile(null);
    setOpen(true);
  };

  const handlePropertyImageSelect = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPropertyImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPropertyImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removePropertyImage = () => {
    setPropertyImageFile(null);
    setPropertyImagePreview(null);
    setFormData({ ...formData, image_url: "" });
  };

  const handleSyncStoryblok = async () => {
    setIsSyncing(true);
    try {
      console.log("Calling edge function: sync-storyblok");

      const { data, error } = await supabase.functions.invoke(
        "sync-storyblok",
        {
          body: {},
        }
      );

      if (error) {
        console.error("Storyblok sync failed:", error);
        throw error;
      }

      console.log("✅ Storyblok sync completed:", data);

      // Refresh properties list, public rooms, and admin room thumbnails so images show
      queryClient.invalidateQueries({ queryKey: ["admin-properties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-all-rooms-thumbnails"],
      });

      toast.success(
        data?.message ||
          t(
            "admin.properties.storyblokSyncSummary",
            "Synced {{properties}} properties and {{rooms}} rooms"
          )
            .replace("{{properties}}", String(data?.synced?.properties || 0))
            .replace("{{rooms}}", String(data?.synced?.rooms || 0)),
        {
          description: data?.errors?.length
            ? t(
                "admin.properties.storyblokSyncErrorsCount",
                "{{count}} error(s) occurred. Check console for details."
              ).replace("{{count}}", String(data.errors.length))
            : undefined,
        }
      );

      if (data?.errors && data.errors.length > 0) {
        console.warn("Sync errors:", data.errors);
      }
    } catch (error: any) {
      console.error("Error syncing from Storyblok:", error);
      toast.error(
        t("admin.properties.syncFailed", "Failed to sync from Storyblok"),
        {
          description:
            error.message ||
            t(
              "admin.properties.syncFailedDesc",
              "Please check your Storyblok configuration and try again."
            ),
        }
      );
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Card className="overflow-hidden border-border/60 shadow-lg shadow-black/5">
      <CardHeader className="border-b border-border/50 bg-muted/20 pb-4 sm:pb-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-between gap-3 sm:gap-4">
          <div>
            <CardTitle className="text-lg sm:text-xl font-semibold tracking-tight text-foreground">
              {t("admin.properties")}
            </CardTitle>
            <CardDescription className="mt-1 text-muted-foreground text-sm">
              {t("admin.propertiesDesc")}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleSyncStoryblok}
              disabled={isSyncing}
            >
              <RefreshCw
                className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`}
              />
              {isSyncing
                ? t("admin.properties.syncing", "Syncing...")
                : t(
                    "admin.properties.syncFromStoryblok",
                    "Sync from Storyblok"
                  )}
            </Button>
            <Dialog
              open={open}
              onOpenChange={(isOpen) => {
                if (isOpen) {
                  setOpen(isOpen);
                } else if (
                  !createMutation.isPending &&
                  !updateMutation.isPending
                ) {
                  setOpen(isOpen);
                  resetForm();
                  setPropertyImagePreview(null);
                  setPropertyImageFile(null);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  className="flex items-center gap-2"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {createMutation.isPending
                        ? t("common.creating", "Creating...")
                        : t("common.updating", "Updating...")}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      {t("admin.addProperty")}
                    </>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingProperty
                      ? t("admin.editProperty")
                      : t("admin.addProperty")}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">{t("admin.propertyTitle")}</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">{t("admin.city")}</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) =>
                          setFormData({ ...formData, city: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address">{t("admin.address")}</Label>
                      <AddressAutocomplete
                        id="address"
                        value={formData.address}
                        onChange={(v) =>
                          setFormData({ ...formData, address: v })
                        }
                        onSelect={({ street, houseNumber, city }) =>
                          setFormData({
                            ...formData,
                            address: houseNumber
                              ? `${street} ${houseNumber}`
                              : street,
                            city: city || formData.city,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="lat">{t("admin.latitude")}</Label>
                      <Input
                        id="lat"
                        type="number"
                        step="any"
                        value={formData.lat === 0 ? "" : formData.lat}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData({
                            ...formData,
                            lat: value === "" ? 0 : parseFloat(value) || 0,
                          });
                        }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lng">{t("admin.longitude")}</Label>
                      <Input
                        id="lng"
                        type="number"
                        step="any"
                        value={formData.lng === 0 ? "" : formData.lng}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData({
                            ...formData,
                            lng: value === "" ? 0 : parseFloat(value) || 0,
                          });
                        }}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="property-image">
                      {t("admin.properties.propertyImage", "Property Image")}
                    </Label>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-2">
                        <Input
                          id="property-image"
                          type="file"
                          accept="image/*"
                          onChange={handlePropertyImageSelect}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            document.getElementById("property-image")?.click()
                          }
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {t("admin.properties.uploadImage", "Upload Image")}
                        </Button>
                        {formData.image_url && !propertyImageFile && (
                          <span className="text-sm text-muted-foreground">
                            {t("admin.properties.current", "Current")}:{" "}
                            {formData.image_url.substring(0, 50)}...
                          </span>
                        )}
                      </div>
                      {(propertyImagePreview || formData.image_url) && (
                        <div className="relative group">
                          <img
                            key={`property-img-${editingProperty?.id || "new"}-${propertyImagePreview || formData.image_url}`}
                            src={
                              propertyImagePreview || formData.image_url || ""
                            }
                            alt={t(
                              "admin.properties.propertyPreviewAlt",
                              "Property preview"
                            )}
                            className="w-full h-48 object-cover rounded-lg border"
                            onError={(e) => {
                              console.error(
                                "Failed to load property image:",
                                propertyImagePreview || formData.image_url
                              );
                              console.error(
                                "Image URL was:",
                                propertyImagePreview || formData.image_url
                              );
                              e.currentTarget.style.display = "none";
                            }}
                            onLoad={() => {
                              console.log(
                                "Property image loaded successfully:",
                                propertyImagePreview || formData.image_url
                              );
                            }}
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={removePropertyImage}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">
                      {t("admin.description")}
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      rows={4}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="virtual-tour-url">
                      {t(
                        "admin.properties.tourUrlLabel",
                        "360° Tour URL (optional)"
                      )}
                    </Label>
                    <Input
                      id="virtual-tour-url"
                      placeholder="https://vtc.virtualtourscreator.com.au/share/..."
                      value={formData.virtual_tour_url}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          virtual_tour_url: e.target.value,
                        })
                      }
                      onBlur={(e) =>
                        setFormData({
                          ...formData,
                          virtual_tour_url: normalizeVirtualTourUrl(
                            e.target.value
                          ),
                        })
                      }
                    />
                  </div>
                  {contractTemplates.length > 0 && (
                    <div className="space-y-2">
                      <Label>
                        {t(
                          "admin.properties.defaultContractTemplate",
                          "Default Contract Template"
                        )}
                      </Label>
                      <Select
                        value={
                          formData.default_contract_template_id || "__none__"
                        }
                        onValueChange={(v) =>
                          setFormData({
                            ...formData,
                            default_contract_template_id:
                              v === "__none__" ? "" : v,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              "admin.properties.useSystemDefault",
                              "Use system default"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">
                            {t(
                              "admin.properties.useSystemDefault",
                              "Use system default"
                            )}
                          </SelectItem>
                          {contractTemplates.map((tpl: any) => (
                            <SelectItem key={tpl.id} value={tpl.id}>
                              {tpl.name}
                              {tpl.is_default
                                ? ` (${t("admin.properties.systemDefault", "system default")})`
                                : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {t(
                          "admin.properties.contractTemplateHint",
                          "Contract template used when generating contracts for tenants in this property."
                        )}
                      </p>
                    </div>
                  )}

                  {/* ── Landlord signature — stamped onto generated contracts at REPRESENTOR_SIGNATURE position ── */}
                  <div className="space-y-2">
                    <Label>
                      {t(
                        "admin.properties.landlordSignature",
                        "Landlord Signature"
                      )}
                    </Label>
                    {formData.leasor_signature_url ? (
                      <div className="rounded-lg border bg-muted/20 p-3 flex items-center gap-3">
                        {landlordSignature.failed ? (
                          <span className="text-xs text-destructive">
                            {t(
                              "admin.templates.signatureLoadFailed",
                              "Could not load the signature image."
                            )}
                          </span>
                        ) : (
                          <img
                            src={landlordSignature.url ?? undefined}
                            alt={t(
                              "admin.properties.landlordSignatureAlt",
                              "Landlord signature"
                            )}
                            className="h-16 w-auto max-w-[220px] rounded-md bg-white border object-contain"
                          />
                        )}
                        <div className="flex-1 flex flex-col gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setLeasorSignatureDialogOpen(true)}
                          >
                            <PenLine className="w-3.5 h-3.5 mr-1.5" />
                            {t("common.replace", "Replace")}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() =>
                              setFormData({
                                ...formData,
                                leasor_signature_url: "",
                              })
                            }
                          >
                            <X className="w-3.5 h-3.5 mr-1.5" />
                            {t("common.remove", "Remove")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => setLeasorSignatureDialogOpen(true)}
                      >
                        <PenLine className="w-4 h-4 mr-2" />
                        {t(
                          "admin.properties.addLandlordSignature",
                          "Add Landlord Signature"
                        )}
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "admin.properties.landlordSignatureHint",
                        "Drawn or uploaded signature — auto-stamped onto contracts at the REPRESENTOR_SIGNATURE position in the template."
                      )}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2"
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                  >
                    {createMutation.isPending || updateMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {createMutation.isPending
                          ? t(
                              "admin.properties.creatingProperty",
                              "Creating Property..."
                            )
                          : t(
                              "admin.properties.updatingProperty",
                              "Updating Property..."
                            )}
                      </>
                    ) : editingProperty ? (
                      t("admin.updateProperty")
                    ) : (
                      t("admin.createProperty")
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 sm:p-6">
        {isLoading || isLoadingRooms ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : listRooms && listRooms.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-xl border border-border/60 bg-card shadow-sm -mx-1 px-1 sm:mx-0 sm:px-0">
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow className="border-border/50 bg-muted/30 hover:bg-muted/30">
                    <TableHead className="py-4 pl-6 font-semibold text-foreground/90">
                      {t("admin.propertyTitle")}
                    </TableHead>
                    <TableHead className="py-4 font-semibold text-foreground/90">
                      {t("admin.city")}
                    </TableHead>
                    <TableHead className="py-4 font-semibold text-foreground/90">
                      {t("admin.address")}
                    </TableHead>
                    <TableHead className="py-4 font-semibold text-foreground/90">
                      {tr("admin.status")}
                    </TableHead>
                    <TableHead className="py-4 font-semibold text-foreground/90">
                      {tr("admin.signedBy")} / {tr("admin.contractPending")}
                    </TableHead>
                    <TableHead className="py-4 font-semibold text-foreground/90">
                      {tr("admin.rent")}
                    </TableHead>
                    <TableHead className="py-4 font-semibold text-foreground/90">
                      {tr("admin.deposit")}
                    </TableHead>
                    <TableHead className="py-4 pr-6 text-right font-semibold text-foreground/90">
                      {t("admin.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRooms.map((room: any) => {
                    const property = room.properties || {};
                    const summary = roomSummaryMap[room.id];
                    const signedCount = summary?.signedCount ?? 0;
                    const pendingCount = summary?.pendingCount ?? 0;
                    const rents = summary?.rents ?? [];
                    const deposits = summary?.deposits ?? [];
                    const rentStr =
                      rents.length > 0
                        ? rents.length === 1
                          ? `€${Number(rents[0]).toLocaleString(language === "de" ? "de-DE" : "en-GB")}/${t("admin.properties.perMonthSuffix", "mo")}`
                          : `€${Math.min(...rents).toLocaleString(language === "de" ? "de-DE" : "en-GB")} – €${Math.max(...rents).toLocaleString(language === "de" ? "de-DE" : "en-GB")}/${t("admin.properties.perMonthSuffix", "mo")}`
                        : room.price != null
                          ? `€${Number(room.price).toLocaleString(language === "de" ? "de-DE" : "en-GB")}/${t("admin.properties.perMonthSuffix", "mo")}`
                          : "—";
                    const depositStr =
                      deposits.length === 0
                        ? "—"
                        : deposits.length === 1
                          ? `€${Number(deposits[0]).toLocaleString("de-DE")}`
                          : `€${Math.min(...deposits).toLocaleString("de-DE")} – €${Math.max(...deposits).toLocaleString("de-DE")}`;
                    const roomThumbnails = normalizeRoomImagesToUrls(
                      room.images
                    );
                    const propertyImg = property.image_url
                      ? fixStoryblokImageUrl(property.image_url)
                      : null;
                    const displayThumbnails = [...roomThumbnails];
                    if (propertyImg && !displayThumbnails.includes(propertyImg))
                      displayThumbnails.push(propertyImg);
                    const displayThumbnailsSlice = displayThumbnails.slice(
                      0,
                      5
                    );
                    return (
                      <TableRow
                        key={room.id}
                        className="group border-border/40 transition-colors hover:bg-muted/20"
                      >
                        <TableCell className="py-4 pl-6 align-top">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold tracking-tight text-foreground">
                                {room.title}
                              </p>
                              {room.is_hidden && (
                                <Badge
                                  variant="secondary"
                                  className="text-xs font-medium text-amber-700 dark:text-amber-400 border-amber-200/50"
                                >
                                  {t("common.hidden", "Hidden")}
                                </Badge>
                              )}
                            </div>
                            {property.title && (
                              <p className="text-xs text-muted-foreground">
                                {property.title}
                              </p>
                            )}
                            {displayThumbnailsSlice.length > 0 && (
                              <div className="flex gap-1.5 flex-wrap">
                                {displayThumbnailsSlice.map((src, i) => (
                                  <div
                                    key={`${room.id}-thumb-${i}`}
                                    className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-border/50 bg-muted shadow-inner ring-1 ring-black/5"
                                  >
                                    <img
                                      src={src}
                                      alt=""
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-4 text-sm text-muted-foreground">
                          {property.city ?? "—"}
                        </TableCell>
                        <TableCell
                          className="max-w-[180px] py-4 text-sm text-muted-foreground truncate"
                          title={property.address ?? ""}
                        >
                          {property.address ?? "—"}
                        </TableCell>
                        <TableCell className="py-4">
                          {signedCount + pendingCount > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {signedCount > 0 && (
                                <Badge className="gap-1 border-0 bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 text-xs font-medium">
                                  <FileCheck className="h-3 w-3" />
                                  {signedCount} {tr("admin.signed")}
                                </Badge>
                              )}
                              {pendingCount > 0 && (
                                <Badge
                                  variant="secondary"
                                  className="text-xs font-medium text-amber-700 dark:text-amber-400 border-amber-200/50 dark:border-amber-500/30"
                                >
                                  {pendingCount} {tr("admin.pending")}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[240px] py-4 text-sm align-top">
                          {(() => {
                            const signedDetails =
                              summary?.signedSignerDetails ?? [];
                            const pendingDetails =
                              summary?.pendingSignerDetails ?? [];
                            const dedupe = (
                              arr: {
                                name: string;
                                userId?: string;
                                bookingId: number;
                              }[]
                            ) => {
                              const seen = new Set<string>();
                              return arr.filter((d) => {
                                const key = d.name + (d.userId ?? "");
                                if (seen.has(key)) return false;
                                seen.add(key);
                                return true;
                              });
                            };
                            const signedUnique = dedupe(signedDetails);
                            const pendingUnique = dedupe(pendingDetails);
                            if (
                              signedUnique.length === 0 &&
                              pendingUnique.length === 0
                            )
                              return (
                                <span className="text-muted-foreground">—</span>
                              );
                            const renderNames = (
                              list: {
                                name: string;
                                userId?: string;
                                bookingId: number;
                              }[],
                              max: number
                            ) => (
                              <>
                                {list.slice(0, max).map((d) => (
                                  <button
                                    key={d.bookingId}
                                    type="button"
                                    onClick={() =>
                                      navigate("/admin?tab=tenants", {
                                        state: { openBookingId: d.bookingId },
                                      })
                                    }
                                    className="inline-flex items-center gap-1 text-primary hover:underline font-medium bg-transparent border-0 p-0 cursor-pointer"
                                    title={t(
                                      "admin.properties.viewUserProfile",
                                      "View user profile"
                                    )}
                                  >
                                    {d.name}
                                    <ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
                                  </button>
                                ))}
                                {list.length > max && (
                                  <span className="text-muted-foreground">
                                    +{list.length - max}
                                  </span>
                                )}
                              </>
                            );
                            return (
                              <div className="space-y-2">
                                {signedUnique.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground mb-0.5">
                                      {tr("admin.signedBy")}
                                    </p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                                      {renderNames(signedUnique, 4)}
                                    </div>
                                  </div>
                                )}
                                {pendingUnique.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground mb-0.5">
                                      {tr("admin.contractPending")}
                                    </p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                                      {renderNames(pendingUnique, 4)}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="whitespace-nowrap py-4 text-sm font-medium text-foreground">
                          {rentStr}
                        </TableCell>
                        <TableCell className="whitespace-nowrap py-4 text-sm font-medium text-foreground">
                          {depositStr}
                        </TableCell>
                        <TableCell className="py-4 pr-6 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                              onClick={() => navigate(`/property/${room.id}`)}
                              title={t(
                                "admin.properties.viewOnSite",
                                "View on site"
                              )}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            {room.is_hidden ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                                onClick={() =>
                                  unhideRoomMutation.mutate(room.id)
                                }
                                disabled={unhideRoomMutation.isPending}
                                title={`${tr("admin.unhide")} (${t("admin.rooms.roomNoun", "room")})`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                                onClick={() => hideRoomMutation.mutate(room.id)}
                                disabled={hideRoomMutation.isPending}
                                title={`${tr("admin.hide")} (${t("admin.rooms.roomNoun", "room")})`}
                              >
                                <EyeOff className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                              onClick={() => handleManageRooms(property.id)}
                              title={t(
                                "admin.properties.manageRooms",
                                "Manage Rooms"
                              )}
                            >
                              <DoorOpen className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                              onClick={() => {
                                const p = properties?.find(
                                  (pr: any) => pr.id === property.id
                                );
                                if (p) handleOpenContractFields(p);
                              }}
                              title={t(
                                "admin.properties.contractFieldsTitle",
                                "Contract fields (landlord, bank, address)"
                              )}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                              onClick={() => {
                                const p = properties?.find(
                                  (pr: any) => pr.id === property.id
                                );
                                if (p) handleEdit(p);
                              }}
                              title={t(
                                "admin.properties.editPropertyTitle",
                                "Edit property"
                              )}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => handleDeleteClick(property.id)}
                              title={t(
                                "admin.properties.deletePropertyTitleAttr",
                                "Delete property"
                              )}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:flex-wrap items-center justify-between gap-3 sm:gap-4 border-t border-border/50 pt-4 sm:pt-6">
                <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left order-2 sm:order-1">
                  {t("common.showing", "Showing")}{" "}
                  <span className="font-medium text-foreground">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>
                  –
                  <span className="font-medium text-foreground">
                    {Math.min(currentPage * itemsPerPage, listRooms.length)}
                  </span>{" "}
                  {t("common.of", "of")}{" "}
                  <span className="font-medium text-foreground">
                    {listRooms.length}
                  </span>{" "}
                  {t("admin.rooms.roomsNoun", "rooms")}
                </p>
                <div className="flex items-center gap-2 flex-wrap justify-center order-1 sm:order-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-w-[90px] border-border/60"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("common.previous", "Previous")}
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={page}
                              variant={
                                currentPage === page ? "default" : "outline"
                              }
                              size="sm"
                              className="min-w-[40px] border-border/60"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Button>
                          );
                        }
                        if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return (
                            <span
                              key={page}
                              className="px-1 text-muted-foreground"
                            >
                              …
                            </span>
                          );
                        }
                        return null;
                      }
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-w-[90px] border-border/60"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    {t("common.next", "Next")}
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="py-12 text-center text-muted-foreground">
            {t("admin.noProperties")}
          </p>
        )}
      </CardContent>

      {/* Room Management Dialog */}
      <Dialog
        open={roomDialogOpen}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            setRoomDialogOpen(isOpen);
          } else if (
            !createRoomMutation.isPending &&
            !updateRoomMutation.isPending
          ) {
            setRoomDialogOpen(isOpen);
            resetRoomForm();
            setSelectedPropertyId(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("admin.properties.manageRooms", "Manage Rooms")} -{" "}
              {properties?.find((p) => p.id === selectedPropertyId)?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Room Form */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-4">
                {editingRoom
                  ? t("admin.rooms.editRoom", "Edit Room")
                  : t("admin.rooms.addNewRoom", "Add New Room")}
              </h3>
              <form onSubmit={handleRoomSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="room-title">
                      {t("admin.rooms.roomTitle", "Room Title")}
                    </Label>
                    <Input
                      id="room-title"
                      value={roomFormData.title}
                      onChange={(e) =>
                        setRoomFormData({
                          ...roomFormData,
                          title: e.target.value,
                        })
                      }
                      placeholder={t(
                        "admin.rooms.roomTitlePlaceholder",
                        "e.g., Premium Room, Studio Apartment"
                      )}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="room-price">
                      {t("admin.rooms.priceEuro", "Price (€)")}
                    </Label>
                    <Input
                      id="room-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={
                        roomFormData.price === 0 ? "" : roomFormData.price || ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setRoomFormData({
                          ...roomFormData,
                          price: value === "" ? 0 : parseFloat(value) || 0,
                        });
                      }}
                      placeholder={t(
                        "admin.rooms.pricePlaceholder",
                        "e.g., 850.00"
                      )}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="room-description">
                    {t("admin.description", "Description")}
                  </Label>
                  <Textarea
                    id="room-description"
                    value={roomFormData.description}
                    onChange={(e) =>
                      setRoomFormData({
                        ...roomFormData,
                        description: e.target.value,
                      })
                    }
                    placeholder={t(
                      "admin.rooms.descriptionPlaceholder",
                      "Describe the room, its features, and amenities..."
                    )}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="room-images">
                    {t("admin.rooms.roomPhotos", "Room Photos")}
                  </Label>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <Input
                        id="room-images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          document.getElementById("room-images")?.click()
                        }
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {t("admin.rooms.uploadPhotos", "Upload Photos")}
                      </Button>
                    </div>
                    {roomImagePreviews.length > 0 && (
                      <div className="grid grid-cols-3 gap-4">
                        {roomImagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`${t("common.preview", "Preview")} ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeImage(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="room-available"
                    checked={roomFormData.available}
                    onChange={(e) =>
                      setRoomFormData({
                        ...roomFormData,
                        available: e.target.checked,
                      })
                    }
                    className="rounded"
                  />
                  <Label htmlFor="room-available">
                    {t("admin.rooms.available", "Available")}
                  </Label>
                </div>
                <Button
                  type="submit"
                  disabled={
                    createRoomMutation.isPending || updateRoomMutation.isPending
                  }
                  className="flex items-center gap-2"
                >
                  {createRoomMutation.isPending ||
                  updateRoomMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {createRoomMutation.isPending
                        ? t("admin.rooms.creatingRoom", "Creating Room...")
                        : t("admin.rooms.updatingRoom", "Updating Room...")}
                    </>
                  ) : (
                    <>
                      {editingRoom
                        ? t("admin.rooms.updateRoom", "Update Room")
                        : t("admin.rooms.addRoom", "Add Room")}
                    </>
                  )}
                </Button>
                {editingRoom && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => resetRoomForm()}
                  >
                    {t("common.cancelEdit", "Cancel Edit")}
                  </Button>
                )}
              </form>
            </div>

            {/* Rooms List */}
            <div>
              <h3 className="font-semibold mb-2">
                {t("admin.rooms.roomsNoun", "Rooms")} ({rooms?.length || 0})
              </h3>
              {rooms && rooms.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t("admin.rooms.titleColumn", "Title")}
                      </TableHead>
                      <TableHead>{t("admin.rooms.price", "Price")}</TableHead>
                      <TableHead>
                        {t("admin.rooms.available", "Available")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("admin.actions", "Actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rooms.map((room: any) => (
                      <TableRow key={room.id}>
                        <TableCell className="font-medium">
                          {room.title}
                        </TableCell>
                        <TableCell>€{room.price}</TableCell>
                        <TableCell>
                          {room.available
                            ? t("common.yes", "Yes")
                            : t("common.no", "No")}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditRoom(room)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteRoomMutation.mutate(room.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  {t(
                    "admin.rooms.noRoomsYet",
                    "No rooms yet. Add your first room above."
                  )}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal — only delete when user clicks Confirm; text in current language */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setPropertyToDelete(null);
        }}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div className="space-y-2 flex-1">
                <AlertDialogTitle className="text-lg">
                  {tr("admin.deletePropertyTitle")}
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>{tr("admin.deletePropertyConfirmMessage")}</p>
                    {propertyToDelete &&
                      properties?.find((p) => p.id === propertyToDelete) && (
                        <p className="font-medium text-foreground">
                          {tr("admin.deletePropertyNameLabel")}: &quot;
                          {
                            properties.find((p) => p.id === propertyToDelete)
                              ?.title
                          }
                          &quot;
                        </p>
                      )}
                    <p className="text-destructive/90 font-medium">
                      {tr("admin.deletePropertyWarning")}
                    </p>
                  </div>
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={() => {
                setDeleteDialogOpen(false);
                setPropertyToDelete(null);
              }}
              disabled={deleteMutation.isPending}
            >
              {tr("admin.deleteCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {tr("admin.deleting")}
                </>
              ) : (
                tr("admin.deleteConfirmDelete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Landlord signature capture (draw / upload) ── */}
      <SignatureDialog
        open={leasorSignatureDialogOpen}
        onOpenChange={setLeasorSignatureDialogOpen}
        onConfirm={handleLeasorSignatureConfirm}
        submitting={uploadingLeasorSignature}
        language={(language === "de" ? "de" : "en") as "en" | "de"}
      />

      {/* ── Contract fields modal ── edits contract-placeholder values only. */}
      {/* Deliberately separate from the main Edit Property dialog because the */}
      {/* main one syncs to Storyblok via manage-properties, and these values */}
      {/* (landlord name, bank details, structured address) are contract-only. */}
      <Dialog
        open={contractFieldsOpen}
        onOpenChange={(o) => {
          if (!savingContractFields) {
            setContractFieldsOpen(o);
            if (!o) setContractFieldsProperty(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("admin.properties.contractFields", "Contract Fields")}
              {contractFieldsProperty?.title && (
                <span className="block text-sm font-normal text-muted-foreground mt-0.5">
                  {contractFieldsProperty.title}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveContractFields} className="space-y-4">
            <p className="text-xs text-muted-foreground">
              {t(
                "admin.properties.contractFieldsHint",
                "These values fill named placeholders in the contract template for tenants at this property. Saved directly to Supabase — not pushed to Storyblok."
              )}
            </p>

            <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
              <div>
                <h4 className="text-sm font-semibold">
                  {t(
                    "admin.properties.landlordContractDetails",
                    "Landlord / Contract Details"
                  )}
                </h4>
                <p className="text-xs text-muted-foreground">
                  OWNER_NAME, REPRESENTOR_NAME, REPRESENTOR_CITY,
                  PROPERTY_STREET_*, PROPERTY_ZIP_CODE, PROPERTY_CITY_NAME
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cf_leasor_name">
                    {t("admin.properties.leasorName", "Leasor name")}
                  </Label>
                  <Input
                    id="cf_leasor_name"
                    placeholder="Coliviq GmbH"
                    value={contractFieldsData.leasor_name}
                    onChange={(e) =>
                      setContractFieldsData({
                        ...contractFieldsData,
                        leasor_name: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf_representor_name">
                    {t("admin.properties.representorName", "Representor name")}
                  </Label>
                  <Input
                    id="cf_representor_name"
                    placeholder="Max Mustermann"
                    value={contractFieldsData.representor_name}
                    onChange={(e) =>
                      setContractFieldsData({
                        ...contractFieldsData,
                        representor_name: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf_representor_city">
                  {t("admin.properties.representorCity", "Representor city")}
                </Label>
                <Input
                  id="cf_representor_city"
                  placeholder="München"
                  value={contractFieldsData.representor_city}
                  onChange={(e) =>
                    setContractFieldsData({
                      ...contractFieldsData,
                      representor_city: e.target.value,
                    })
                  }
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="cf_street_name">
                    {t("admin.properties.streetName", "Street name")}
                  </Label>
                  <Input
                    id="cf_street_name"
                    placeholder="Daimlerstrasse"
                    value={contractFieldsData.street_name}
                    aria-invalid={
                      !!validateStreetName(contractFieldsData.street_name)
                    }
                    onChange={(e) =>
                      setContractFieldsData({
                        ...contractFieldsData,
                        street_name: e.target.value,
                      })
                    }
                  />
                  {validateStreetName(contractFieldsData.street_name) && (
                    <p className="text-xs text-destructive">
                      {validateStreetName(contractFieldsData.street_name)}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf_street_number">
                    {t("admin.properties.streetNumber", "Street number")}
                  </Label>
                  <Input
                    id="cf_street_number"
                    placeholder="42"
                    value={contractFieldsData.street_number}
                    onChange={(e) =>
                      setContractFieldsData({
                        ...contractFieldsData,
                        street_number: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cf_zip_code">
                    {t("admin.properties.zipCode", "ZIP code")}
                  </Label>
                  <Input
                    id="cf_zip_code"
                    placeholder="86391"
                    value={contractFieldsData.zip_code}
                    onChange={(e) =>
                      setContractFieldsData({
                        ...contractFieldsData,
                        zip_code: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf_city_name">
                    {t("admin.properties.cityName", "City name")}
                  </Label>
                  <Input
                    id="cf_city_name"
                    placeholder="Augsburg"
                    value={contractFieldsData.city_name}
                    onChange={(e) =>
                      setContractFieldsData({
                        ...contractFieldsData,
                        city_name: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
              <div>
                <h4 className="text-sm font-semibold">
                  {t("admin.properties.bankDetails", "Bank Details")}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "admin.properties.bankDetailsHint",
                    "ACCOUNT_NAME, IBAN, BIC, BANK_NAME — saved to every room in this property (room_payment_config)."
                  )}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf_bank_account_holder">
                  {t("admin.properties.accountHolder", "Account holder")}
                </Label>
                <Input
                  id="cf_bank_account_holder"
                  placeholder="Coliviq GmbH"
                  value={contractFieldsData.bank_account_holder}
                  onChange={(e) =>
                    setContractFieldsData({
                      ...contractFieldsData,
                      bank_account_holder: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf_bank_iban">
                  {t("admin.properties.iban", "IBAN")}
                </Label>
                <Input
                  id="cf_bank_iban"
                  placeholder="DE89 3704 0044 0532 0130 00"
                  value={contractFieldsData.bank_iban}
                  onChange={(e) =>
                    setContractFieldsData({
                      ...contractFieldsData,
                      bank_iban: e.target.value,
                    })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cf_bank_bic">
                    {t("admin.properties.bic", "BIC")}
                  </Label>
                  <Input
                    id="cf_bank_bic"
                    placeholder="COBADEFFXXX"
                    value={contractFieldsData.bank_bic}
                    onChange={(e) =>
                      setContractFieldsData({
                        ...contractFieldsData,
                        bank_bic: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf_bank_name">
                    {t("admin.properties.bankName", "Bank name")}
                  </Label>
                  <Input
                    id="cf_bank_name"
                    placeholder="Commerzbank"
                    value={contractFieldsData.bank_name}
                    onChange={(e) =>
                      setContractFieldsData({
                        ...contractFieldsData,
                        bank_name: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setContractFieldsOpen(false);
                  setContractFieldsProperty(null);
                }}
                disabled={savingContractFields}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button type="submit" disabled={savingContractFields}>
                {savingContractFields ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("common.saving", "Saving...")}
                  </>
                ) : (
                  t("common.save", "Save")
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
